/**
 * Keeps the address bar and the filter bar saying the same thing.
 *
 * Reading happens once, after the data is loaded — names cannot be turned into
 * tags, plans and goals before those exist.
 *
 * Writing pushes a history entry, so Back walks you through the views you
 * actually looked at. The thing to avoid is an entry per keystroke, and the way
 * to avoid it is not to record nothing: a change to the same field again a
 * moment later replaces the entry instead of adding one, so typing "lidl" is a
 * single view rather than four, and Back from it lands on the search you had
 * before.
 */
import { watch } from 'vue'
import { decodeView, encodeView, kindWord, NOTHING, wordKind } from '../lib/url'
import { NONE, setFilter } from './index'
import { state } from './state'

// What a row is called in the query string: a tag goes by its name, a plan by
// its category, since that is what a plan is.
const labelled = (row) => row?.name ?? row?.category ?? ''

const named = (list, value) =>
  list.find((row) => labelled(row).toLowerCase() === value.toLowerCase())

function toIds(names, list) {
  return names
    .map((name) => (name.toLowerCase() === NOTHING ? NONE : named(list, name)?.id))
    .filter(Boolean)
}

const toNames = (ids, list) =>
  ids
    .map((id) => (id === NONE ? NOTHING : labelled(list.find((row) => row.id === id))))
    .filter(Boolean)

/** The address bar as the app sees it. */
export function readView(query) {
  const view = decodeView(query)
  return {
    search: view.search,
    sort: view.sort,
    kinds: view.kinds.map(wordKind).filter(Boolean),
    status: view.status,
    files: view.files,
    tags: toIds(view.tags, state.tags),
    // A category is its own name, so there is nothing to look up — but
    // "nothing" still means the uncategorised ones.
    categories: view.categories.map((name) =>
      name.toLowerCase() === NOTHING ? NONE : name,
    ),
    from: view.from,
    to: view.to,
    amountFrom: view.amountFrom,
    amountTo: view.amountTo,
  }
}

/** What the address bar should say about the app. */
export function writeView() {
  return encodeView({
    search: state.ui.search,
    sort: state.ui.sort,
    kinds: state.ui.kinds.map(kindWord),
    status: state.ui.status,
    files: state.ui.files,
    tags: toNames(state.ui.tags, state.tags),
    categories: state.ui.categories.map((id) => (id === NONE ? NOTHING : id)),
    from: state.ui.from,
    to: state.ui.to,
    amountFrom: state.ui.amountFrom,
    amountTo: state.ui.amountTo,
  })
}

export function applyView(view) {
  state.ui.search = view.search
  Object.assign(state.ui.sort, view.sort)
  setFilter('kinds', view.kinds)
  setFilter('status', view.status)
  setFilter('files', view.files)
  setFilter('tags', view.tags)
  setFilter('categories', view.categories)
  setFilter('from', view.from)
  setFilter('to', view.to)
  setFilter('amountFrom', view.amountFrom)
  setFilter('amountTo', view.amountTo)
}

/** How long a field goes on being "the same edit" rather than the next one. */
const SAME_EDIT_MS = 800

/** The query as a map, so two of them can be compared key by key. */
const params = (query) =>
  new Map(
    query
      .replace(/^\?/, '')
      .split('&')
      .filter(Boolean)
      .map((pair) => {
        const at = pair.indexOf('=')
        return at < 0 ? [pair, ''] : [pair.slice(0, at), pair.slice(at + 1)]
      }),
  )

/** Which parts of the view moved, as a stable signature. */
function changedKeys(before, after) {
  const [a, b] = [params(before), params(after)]
  return [...new Set([...a.keys(), ...b.keys()])]
    .filter((key) => a.get(key) !== b.get(key))
    .sort()
    .join(',')
}

/** Reads the URL once, then keeps it up to date. Browser-only. */
export function startUrlSync() {
  if (typeof window === 'undefined') return () => {}
  applyView(readView(window.location.search))

  const href = (query) => `${window.location.pathname}${query}${window.location.hash}`
  let lastKeys = ''
  let lastAt = 0

  // Whatever the link said, the app may read it slightly differently — a tag
  // that has since been deleted, say. Settling that is tidying the entry we
  // arrived on, not a move away from it.
  const settled = writeView()
  if (settled !== window.location.search) window.history.replaceState(null, '', href(settled))

  const write = () => {
    const query = writeView()
    const current = window.location.search
    if (query === current) return
    const keys = changedKeys(current, query)
    const now = Date.now()
    // The same field moving again within a breath is one decision still being
    // made: a search being typed, an amount being nudged.
    const continuing = keys === lastKeys && now - lastAt < SAME_EDIT_MS
    window.history[continuing ? 'replaceState' : 'pushState'](null, '', href(query))
    lastKeys = keys
    lastAt = now
  }
  const stop = watch(() => writeView(), write)

  // Back, forward, or someone editing the address bar. Applying the view makes
  // `writeView()` agree with the URL again, so the watcher writes nothing back.
  const onPop = () => {
    applyView(readView(window.location.search))
    lastKeys = ''
  }
  window.addEventListener('popstate', onPop)
  return () => {
    stop()
    window.removeEventListener('popstate', onPop)
  }
}
